import { createFileRoute, Link } from "@tanstack/react-router";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { Capabilities } from "@/components/calv/Capabilities";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { jsonLdScript, serviceSchema, breadcrumbSchema, pageHead } from "@/lib/seo";

export const Route = createFileRoute("/leistungen/social-media-marketing")({
  head: () => {
    const h = pageHead("socialMediaMarketing", "de");
    return {
      meta: h.meta,
      links: h.links,
      scripts: [
        jsonLdScript(serviceSchema({
          name: "Social Media Marketing — Organic & Paid integriert",
          description: "Methodisches Social Media Marketing für Mittelstand und E-Commerce. Organic und Paid integriert über Meta, TikTok, LinkedIn und Pinterest.",
          url: "/leistungen/social-media-marketing",
          serviceType: "Social Media Marketing",
        })),
        jsonLdScript(breadcrumbSchema([
          { name: "Start", path: "/" },
          { name: "Leistungen", path: "/leistungen" },
          { name: "Social Media Marketing", path: "/leistungen/social-media-marketing" },
        ])),
      ],
    };
  },
  component: SocialMediaMarketingDE,
});

const platforms = [
  { tag: "Meta", title: "Instagram & Facebook", body: "Breite B2C-Reichweite, starke Targeting-Mechanik, integrierte Shopping-Layer. Erste Wahl für E-Commerce mit AOV 30–300 €, visualisierbaren Produkten und Bildsprache mit Stop-Power. Reels übernehmen in Tier-1-Mandaten 60–80 % der Reach-Generierung." },
  { tag: "TikTok", title: "Junge Zielgruppen", body: "Hohes Organic-Reach-Potential bei niedrigem CPM, dominant in 18–34. Sinnvoll für Marken mit kreativer Content-Kapazität und Toleranz für experimentellen Stil. Algorithmus belohnt Frequenz und Authentizität — Hochglanz performt häufig schlechter als kontextueller Content." },
  { tag: "LinkedIn", title: "B2B-Decision-Maker", body: "Dominanter B2B-Kanal, vor allem für Sales-Cycle zwischen 3 und 18 Monaten. Erste Wahl für komplexe Dienstleistungen, Software-Vertrieb und High-Ticket-Mandate. Geschäftsführer-Sichtbarkeit wirkt substanziell stärker als Marken-Account-Sichtbarkeit." },
  { tag: "Pinterest", title: "Inspirations-Search", body: "Unterschätzter Kanal mit langfristiger Such-Mechanik — Pins bleiben 6–12 Monate auffindbar. Methodisch substantiell für Lifestyle, Wohnen, Hochzeit, Mode, DIY, Garten — überall, wo Inspirations-Search noch substantielle Volumina hat." },
];

const capabilities = [
  { name: "Plattform-Strategie", detail: "Zielgruppen-Fit statt Trend-Logik" },
  { name: "Organic-Content-Pflege", detail: "3–7 Posts/Woche, plattform-spezifisch" },
  { name: "Paid-Verstärkung", detail: "50–70 % Budget auf Organic-Performer" },
  { name: "Creator- & UGC-Strategie", detail: "Substanzielle Auswahl, nicht Streuung" },
  { name: "Server-Side Conversion-Tracking", detail: "CAPI · Conversions API · Events API" },
  { name: "Attribution & Inkrementalität", detail: "DDA, Brand-Lift, Geo-Holdouts" },
  { name: "Creative-Refresh-Pipeline", detail: "Monatlicher Rhythmus, picazzoo-gestützt" },
  { name: "Wirtschaftliche Vorab-Klärung", detail: "AOV, Funnel, Plattform-Fit" },
];

const budgetSplit = [
  ["50–70 %", "Paid-Verstärkung gut-performenden Organic-Contents — statt blindes Paid-Posting neuer Creatives."],
  ["20–30 %", "Strategisch geplante Paid-Aktivierung — Produkt-Launches, Saisonalität, Eskalationen."],
  ["10–20 %", "Test-Reserve für neue Formate, neue Audiences, neue Plattform-Features."],
];

const velocity = [
  { tag: "Instagram", title: "3–5 Posts + 1–2 Reels/Woche", body: "Stabile Reach-Entwicklung in der Plattform-Logik 2026 — Reels dominieren die Reichweite, statische Posts halten die Marke." },
  { tag: "TikTok", title: "4–7 Videos/Woche", body: "Substantielle Algorithmus-Sichtbarkeit. Frequenz und Authentizität schlagen Produktions-Glanz — kontextueller Content vor Hochglanz." },
  { tag: "LinkedIn", title: "3–5 Posts/Woche", body: "Unternehmens-Seite plus 1–3 persönliche Accounts der Schlüssel-Personen. Geschäftsführer-Sichtbarkeit als zusätzlicher Trust-Hebel." },
  { tag: "Pinterest", title: "5–10 Pins/Woche", body: "Ausgerichtet auf saisonale Such-Patterns. Pins bleiben monatelang sichtbar — Aufwand amortisiert sich langfristig." },
];

const notFit = [
  { tag: "Passt nicht", title: "Hochpreisiges B2B mit kleinem TAM", body: "Wenn die Zielgruppe aus 200 Entscheidern in DACH besteht, ist Account-Based-Vertrieb plus Conference-Präsenz wirksamer. LinkedIn flankiert, ist aber nicht das Primär-Werkzeug." },
  { tag: "Passt nicht", title: "Commodity ohne visuelle Differenzierung", body: "Social Media lebt von visueller Substanz. Such-orientiertes Performance-Marketing (SEA, SEO) kompensiert fehlende visuelle Differenzierung über den Such-Intent." },
  { tag: "Passt nicht", title: "Stark regulierte Branchen", body: "Pharma, Tabak, Glücksspiel, Waffen, Teile des Finanzwesens — plattform-seitige Werberichtlinien lassen oft keinen substantiellen Spielraum für wirtschaftliche Steuerung." },
];

const figures = [
  ["3–7", "Posts/Woche typische Content-Velocity"],
  ["1–50 K€", "monatliche Paid-Budgets in Mandaten"],
  ["2–8 %", "Engagement-Rate auf Instagram in Tier-1-Mandaten"],
  ["+30–60 %", "Attributions-Recovery durch Server-Side-CAPI"],
];

function SocialMediaMarketingDE() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          number="01"
          eyebrow="Social Media Marketing · seit 2013"
          headline={<>Social Media, <em>integriert gedacht.</em></>}
          lead="Organic und Paid Social sind keine zwei Disziplinen, sondern zwei Hebel einer integrierten Mechanik. Wir betreiben Social Media Marketing auf Meta, TikTok, LinkedIn und Pinterest als gemeinsames System — kontrolliert, datengeführt, ohne Black-Box-Versprechen."
          metaStrip={[
            "3–7 Posts/Woche typische Velocity",
            "1–50 K€ monatliche Paid-Budgets",
            "Karlstadt am Main · bundesweit · DACH",
          ]}
          breadcrumbs={[
            { label: "Start", to: "/" },
            { label: "Leistungen", to: "/leistungen" },
            { label: "Social Media Marketing" },
          ]}
        />

        <Section number="02" kicker="Standpunkt" headline={<>Was Social Media Marketing <em>methodisch ist.</em></>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">Soziale Plattformen funktionieren nicht über Reichweite allein. Sie funktionieren über drei integrierte Hebel: <strong className="font-semibold text-ink">Content-Substanz</strong> (was gesagt wird), <strong className="font-semibold text-ink">Distribution-Mechanik</strong> (organische Reichweite plus Paid-Verstärkung) und <strong className="font-semibold text-ink">Community-Dichte</strong> (wer interagiert, wer teilt, wer empfiehlt). Wer einen der drei Hebel weglässt, betreibt Symbolpolitik — bunte Posts ohne wirtschaftliche Wirkung.</p>
              <p className="mb-5">Calvarius arbeitet im Social-Media-Setup von der wirtschaftlichen Vorab-Klärung aus. Bevor Content-Kalender oder Paid-Budgets diskutiert werden, klärt sich: trägt das Geschäftsmodell die Social-Media-Mechanik überhaupt? Welche Plattformen passen zur Zielgruppe? Wie sieht der Funnel von Social-Touchpoint zum Verkauf aus, und welcher Anteil davon ist überhaupt sauber messbar?</p>
              <p>Diese Reihenfolge unterscheidet methodische Social-Media-Arbeit von operativem Posting. Wer mit dem Content-Kalender beginnt, hat die wirtschaftliche Frage übersprungen und arbeitet ab der ersten Woche mit unverifizierter Annahme.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="03" kicker="Plattform-Strategie" headline={<>Vier Plattformen, <em>vier Substanz-Profile.</em></>}
          caption="Nicht jede Marke gehört auf jede Plattform — Plattform-Wahl folgt Zielgruppen-Realität, nicht Trend.">
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2">
              {platforms.map((p) => (
                <div key={p.title} className="bg-white p-7">
                  <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">{p.tag}</div>
                  <div className="mt-3 font-display text-xl text-ink">{p.title}</div>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{p.body}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-ink-3">
              <strong className="font-semibold text-ink">Snapchat, Reddit, X, Bluesky.</strong> Nur dort sinnvoll, wo die Zielgruppe tatsächlich kauf-affin aktiv ist. Pauschalpräsenz auf allen Plattformen ist Streuverlust, nicht Strategie.
            </p>
            <div className="mt-6 flex flex-wrap gap-6">
              <Link to="/leistungen/performance-marketing/meta-ads" className="font-mono text-xs uppercase tracking-wider text-cyan-deep hover:underline">Meta Ads — Vertiefung →</Link>
              <Link to="/leistungen/performance-marketing/tiktok-ads" className="font-mono text-xs uppercase tracking-wider text-cyan-deep hover:underline">TikTok Ads — Vertiefung →</Link>
              <Link to="/leistungen/performance-marketing/linkedin-ads" className="font-mono text-xs uppercase tracking-wider text-cyan-deep hover:underline">LinkedIn Ads — Vertiefung →</Link>
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="04" kicker="Organic & Paid" headline={<>Organic und Paid als <em>integrierte Mechanik.</em></>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">Die Trennung zwischen Organic Social und Paid Social ist eine operative Konvention, kein methodischer Unterschied. Beide bewegen sich im selben Algorithmen-Raum, beide brauchen substantiellen Content, beide werden über die gleichen KPIs gemessen. Wer sie strikt trennt, verliert die Hebel-Wirkung beider Seiten.</p>
            </div>
            <div className="mt-8 grid gap-px overflow-hidden rounded-lg border border-rule bg-rule md:grid-cols-3">
              {budgetSplit.map(([share, body]) => (
                <div key={share} className="bg-white p-7">
                  <div className="font-display text-2xl text-cyan-deep">{share}</div>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{body}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-ink-3">Diese Aufteilung schützt vor zwei häufigen Fehlern: vor „wir machen nur Organic" (geringer Reach, hohe Abhängigkeit von Algorithmus-Zufall) und vor „wir machen nur Paid" (kein authentischer Marken-Aufbau, hohe CPM-Abhängigkeit, fragile Performance bei Plattform-Updates).</p>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="05" kicker="Content-Velocity" headline={<>Bandbreiten aus <em>laufenden Mandaten.</em></>}
          caption="Substanz-Indikatoren, keine Garantien — wer deutlich darunter liegt, verliert über Wochen messbar Reach.">
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2">
              {velocity.map((v) => (
                <div key={v.title} className="bg-white p-7">
                  <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">{v.tag}</div>
                  <div className="mt-3 font-display text-xl text-ink">{v.title}</div>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{v.body}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-ink-3">Bei Creator-Strategien (UGC, Influencer-Kooperationen, Brand-Ambassadors) gilt die methodische Substanz-Regel: substantielle Auswahl statt Reichweiten-Maximierung. <strong className="font-semibold text-ink">Drei verlässliche Creator über sechs Monate</strong> ist wirksamer als zwanzig One-Off-Kooperationen — Authentizität entsteht durch Wiederholung, nicht durch Streuung.</p>
          </RevealOnScroll>
        </Section>

        <Section number="06" kicker="Performance & Attribution" headline={<>Der attributions-schwierigste Kanal — <em>methodisch beherrschbar.</em></>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">Click-Through-Rates sind oft niedrig, View-Through-Effekte hoch, Cross-Device-Journeys dominant. <em>Last-Click-Attribution unterschätzt Social Media systematisch um 30–60 %</em> — wer Performance ausschließlich nach Last-Click-Logik bewertet, kommt zwangsläufig zur falschen Investitions-Entscheidung.</p>
              <p className="mb-3">Methodisch substantielle Performance-Messung im Social-Media-Mandat:</p>
              <ul className="mb-5 space-y-2 text-[15px] text-ink-2">
                <li>• Datengetriebene oder positions-basierte Attribution statt Last-Click</li>
                <li>• Conversions API (CAPI) für Meta, vergleichbare Server-Side-Integrationen für TikTok und LinkedIn</li>
                <li>• Brand-Lift-Studien für High-Reach-Kampagnen mit substantiellem Budget</li>
                <li>• Inkrementalitäts-Tests (Geo-Holdouts) für budget-relevante Entscheidungen</li>
                <li>• Cross-Channel-Sicht auf den Funnel — Social ist selten der einzige Touchpoint</li>
              </ul>
              <p>→ Vertiefung zu Tracking-Substanz: <Link to="/tools/performance-marketing-checkliste" className="underline">Performance-Marketing-Checkliste, Phase 02</Link></p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="07" kicker="Wirtschaftliche Vorab-Klärung" headline={<>Vier Fragen, <em>bevor Budget fließt.</em></>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5"><strong className="font-semibold text-ink">AOV-Tragfähigkeit.</strong> Trägt der durchschnittliche Bestellwert die plattform-typischen CPMs (1–15 €) und CPCs (0,30–3 €) bei der nötigen Conversion-Rate? Bei AOV unter 30 € wird die Rechnung in den meisten Branchen schwierig.</p>
              <p className="mb-5"><strong className="font-semibold text-ink">Funnel-Mechanik.</strong> Gibt es einen substantiellen Conversion-Pfad von Social zu Kauf? Oder ist Social ein reiner Awareness-Kanal in einem längeren Funnel, der über andere Kanäle abgeschlossen wird? Beides ist methodisch zulässig — aber die Erwartungs-Definition muss zur Mechanik passen.</p>
              <p className="mb-5"><strong className="font-semibold text-ink">Content-Kapazität.</strong> Hat das Unternehmen die Ressourcen für die nötige Content-Frequenz — intern, agenturseitig oder beim Creator? Ohne diese Substanz wird auch das beste Setup nicht tragen.</p>
              <p className="mb-5"><strong className="font-semibold text-ink">Plattform-Fit.</strong> Ist die Zielgruppe auf der avisierten Plattform tatsächlich aktiv und kauf-affin, oder ist sie dort nur als passiver Konsument unterwegs? Aktivität und Kauf-Bereitschaft sind nicht dasselbe.</p>
              <p>→ Werkzeug für die Vorab-Klärung: <Link to="/tools/wirtschaftlichkeitsrechner" className="underline">Wirtschaftlichkeits-Rechner</Link></p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="08" kicker="Wann nicht" headline={<>Wann Social Media Marketing <em>nicht der richtige Hebel ist.</em></>}
          caption="Ehrliche Begrenzungs-Aussagen gehören zur Calvarius-Initial-Beratung.">
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule md:grid-cols-3">
              {notFit.map((a) => (
                <div key={a.title} className="bg-white p-7">
                  <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-2">{a.tag}</div>
                  <div className="mt-3 font-display text-xl text-ink">{a.title}</div>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{a.body}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="09" kicker="Was wir liefern" headline={<>Was unsere <em>Social-Media-Steuerung</em> konkret enthält.</>}>
          <Capabilities items={capabilities} />
        </Section>

        <Section number="10" kicker="Mandate in Zahlen" headline={<>Vier Kennziffern aus <em>laufenden Mandaten.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule sm:grid-cols-2 md:grid-cols-4">
              {figures.map(([n, label]) => (
                <div key={label} className="bg-white p-7">
                  <div className="font-display text-3xl text-cyan-deep">{n}</div>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{label}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="11" kicker="Konkret werden">
          <CTABlock
            headline={<>Mehr Budget ist keine Strategie. <em>Methodik ist es.</em></>}
            body="In einem ersten Gespräch klären wir, ob Social Media Marketing für Ihre wirtschaftliche Konstellation der richtige Hebel ist — und wenn nicht, sagen wir das ehrlich und empfehlen Alternativen."
            primary={{ label: "Strategiegespräch anfragen", to: "/kontakt" }}
            secondary={{ label: "Methodische Werkzeuge testen", to: "/tools" }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
}
